import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Modal } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from '../../component/loader';
import { FiChevronLeft as ChevronLeft, FiChevronRight as ChevronRight } from "react-icons/fi";
import axios from "axios";
import Cookies from "js-cookie";
import { decrpt } from "../../component/encryption";

function UserList() {  
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [users, setUsers] = useState([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");

  // Status modal states
  const [statusModalShow, setStatusModalShow] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const handleStatusClick = (user) => {
    setSelectedUser(user);
    setStatusModalShow(true);
  };

  const handleStatusClose = () => {
    if (!updatingStatus) {
      setStatusModalShow(false);
      setSelectedUser(null);
    }
  };

  const updateUserStatus = async () => {
    if (!selectedUser) return;
    setUpdatingStatus(true);
    const newStatus = selectedUser.status == 1 ? 2 : 1;
    try {
      const token = Cookies.get('s_l');  
      const newToken = decrpt(token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      const res = await axios.post(`/admin/user/status`, {
        id: selectedUser.id,
        status: newStatus
      });
      
      if (res.data?.success) {
        toast.success(res.data.message || `User ${newStatus === 1 ? 'Enabled' : 'Disabled'} successfully!`, { hideProgressBar: true });
        setUsers(prev => prev.map(u => u.id === selectedUser.id ? { ...u, status: newStatus } : u));
        setStatusModalShow(false);
        setSelectedUser(null);
      } else {
        toast.error(res.data?.message || "Failed to update status", { hideProgressBar: true });
      }
    } catch (err) {
      console.error("Failed to update user status:", err);
      toast.error(err.response?.data?.message || "Failed to update user status", { hideProgressBar: true });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const fetchUsers = async () => {
    setLoader(true);
    try {
      const token = Cookies.get('s_l');  
      const newToken = decrpt(token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      const response = await axios.get(`/admin/user/list?all=1`);
      
      if (response.data?.success && response.data?.data) {
        let list = response.data.data.list || [];
        const pagination = response.data.data.pagination;
        
        // If server returned a paginated slice and has more pages, fetch the remaining pages
        if (pagination && pagination.total_pages > 1 && list.length < pagination.total_data) {
          const pagePromises = [];
          for (let p = 2; p <= pagination.total_pages; p++) {
            pagePromises.push(axios.get(`/admin/user/list?page=${p}`));
          }
          const extraResponses = await Promise.all(pagePromises);
          extraResponses.forEach(r => {
            if (r.data?.success && Array.isArray(r.data.data?.list)) {
              list = list.concat(r.data.data.list);
            }
          });
        }
        setUsers(list);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoader(false);
    }
  };

  const userIdSend = (item) => {
    navigate('./single', { state: { item } });
  };

  // Filtered users by search
  const filteredUsers = useMemo(() => {
    if (!Array.isArray(users)) return [];
    if (!searchText.trim()) return users;
    const q = searchText.trim().toLowerCase();
    return users.filter((u) => {
      const name = (u.user_name || u.name || "").toLowerCase();
      const mobile = (u.mobile || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      const id = String(u.id || "").toLowerCase();
      return name.includes(q) || mobile.includes(q) || email.includes(q) || id.includes(q);
    });
  }, [users, searchText]);

  // Pagination logic
  const totalItems = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  // Keep currentPage within valid range whenever totalPages or itemsPerPage changes
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, currentPage, itemsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchUsers();
  }, []);

  return (
    <div className="main-content">
      <ToastContainer position="top-center" autoClose={3000} hideProgressBar={false} theme={'colored'} />
      {loader ? (
        <Loader />
      ) : (
        <div className="section">
          <div className="row">
            <div className="col-md-12">
              <div className="card p-2">
                <div className="card-title d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <h5>All Users</h5>
                  <div style={{ maxWidth: '300px', width: '100%' }}>
                    <input
                      type="text"
                      placeholder="Search by Mobile, Name, Email, ID..."
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="form-control form-control-sm"
                      style={{ border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem' }}
                    />
                  </div>
                </div>
                <div>
                  <table className='table table-bordered'>
                    <thead>
                      <tr>
                        <th>SL</th>
                        <th>User Name</th>
                        <th className="text-center">Mobile Number</th>
                        <th className="text-center">Email Id</th>
                        <th className="text-center">Date of Registration</th>
                        <th className="text-center">Status</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers && paginatedUsers.length > 0 ? (
                        paginatedUsers.map((item, index) => {
                          return (
                            <tr key={index}>
                              <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                              <td>{item.user_name || item.name}</td>
                              <td className="text-center">{item.mobile}</td>
                              <td className="text-center">{item.email}</td>
                              <td className="text-center">{item.created_at}</td>
                              <td className="text-center">
                                {item && item.status == 1 ? (
                                  <Badge bg="success">Enable</Badge>
                                ) : (
                                  <Badge bg="danger">Disable</Badge>
                                )}
                              </td>
                              <td className="text-center">
                                <button onClick={() => userIdSend(item.id)} className='btn btn-xs btn-primary text-white me-1'>
                                  View
                                </button>
                                {item && item.status == 1 ? (
                                  <button
                                    type="button"
                                    className='btn btn-xs btn-danger text-white'
                                    onClick={() => handleStatusClick(item)}
                                    title="Disable this user"
                                  >
                                    Disable
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className='btn btn-xs btn-success text-white'
                                    onClick={() => handleStatusClick(item)}
                                    title="Enable this user"
                                  >
                                    Enable
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center">No data found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Pagination Controls */}
                  {totalItems > 0 && (
                    <div
                      className="flex items-center justify-between mt-4 px-2"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginTop: '1rem',
                        padding: '0 0.5rem',
                        flexWrap: 'wrap',
                        gap: '0.75rem'
                      }}
                    >
                      <div className="flex items-center gap-2" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span className="text-sm text-gray-700" style={{ fontSize: '0.875rem', color: '#374151' }}>Show</span>
                        <select
                          value={itemsPerPage}
                          onChange={handleItemsPerPageChange}
                          className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black"
                          style={{
                            padding: '0.25rem 0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            outline: 'none',
                            background: '#fff'
                          }}
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                        </select>
                        <span className="text-sm text-gray-700" style={{ fontSize: '0.875rem', color: '#374151' }}>entries</span>
                      </div>

                      <div className="flex items-center gap-2" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <button
                          type="button"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="flex items-center gap-1 px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors text-black font-medium"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.25rem 0.75rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            background: '#fff',
                            color: '#000',
                            opacity: currentPage === 1 ? 0.5 : 1,
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                          }}
                        >
                          <ChevronLeft className="h-4 w-4" style={{ height: '1rem', width: '1rem' }} /> Previous
                        </button>

                        <span
                          className="text-sm font-medium px-2 text-gray-700"
                          style={{ fontSize: '0.875rem', fontWeight: 500, padding: '0 0.5rem', color: '#374151' }}
                        >
                          Page {currentPage} of {totalPages}
                        </span>

                        <button
                          type="button"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="flex items-center gap-1 px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors text-black font-medium"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.25rem 0.75rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            background: '#fff',
                            color: '#000',
                            opacity: currentPage === totalPages ? 0.5 : 1,
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Next <ChevronRight className="h-4 w-4" style={{ height: '1rem', width: '1rem' }} />
                        </button>
                      </div>

                      <div className="text-sm text-gray-700" style={{ fontSize: '0.875rem', color: '#374151' }}>
                        Showing {totalItems === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Confirmation Modal */}
      <Modal size="md" show={statusModalShow} onHide={handleStatusClose} centered>
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="h6 mb-0">
            {selectedUser && selectedUser.status == 1 ? 'Disable User' : 'Enable User'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-3">
          Are you sure you want to <strong>{selectedUser && selectedUser.status == 1 ? 'Disable' : 'Enable'}</strong> user{' '}
          <span className="text-danger fw-bold">{selectedUser && (selectedUser.user_name || selectedUser.name)}</span>?
          {selectedUser && selectedUser.status == 1 && (
            <p className="text-muted small mt-2 mb-0">
              Disabling this account will prevent the user from logging in and accessing their account.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer className="p-2 justify-content-end">
          <button onClick={handleStatusClose} className="btn btn-light btn-sm" disabled={updatingStatus}>
            Cancel
          </button>
          <button
            className={`btn btn-sm text-white ${selectedUser && selectedUser.status == 1 ? 'btn-danger' : 'btn-success'}`}
            onClick={updateUserStatus}
            disabled={updatingStatus}
          >
            {updatingStatus ? "Updating..." : (selectedUser && selectedUser.status == 1 ? 'Make Disable' : 'Make Enable')}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default UserList;
